import { type OnChangeFn, type Row, type SortingState, getCoreRowModel } from "@tanstack/react-table"
import type { FC, RefObject } from "react"
import type { CombinedError } from "urql"

import { useProductInventoryColumns } from "../../hooks"

import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import type { InventoryByProductItem } from "@/graphql/codegen/graphql"

interface ProductInventoryTableProps {
  data: InventoryByProductItem[]
  loading?: boolean
  error?: CombinedError | Error
  sorting: SortingState
  containerRef?: RefObject<HTMLDivElement>
  canMaintain: boolean
  onSortingChange: OnChangeFn<SortingState>
  onRowClick?: (row: Row<InventoryByProductItem>) => void
  onAdjust?: (item: InventoryByProductItem) => void
  onTransfer?: (item: InventoryByProductItem) => void
  onProductView?: (item: InventoryByProductItem) => void
  onCoaView?: (item: InventoryByProductItem) => void
  onScroll?: () => void
}

export const ProductInventoryTable: FC<ProductInventoryTableProps> = ({
  data,
  error,
  loading = false,
  sorting,
  containerRef,
  canMaintain,
  onSortingChange,
  onRowClick,
  onAdjust,
  onTransfer,
  onProductView,
  onCoaView,
  onScroll,
}) => {
  const columns = useProductInventoryColumns({ canMaintain, onAdjust, onTransfer, onProductView, onCoaView })

  return (
    <AerosBaseTable
      error={error}
      features={{
        table: {
          stickyHeader: true,
          flexibleColumnSizing: true,
        },
        tanstackOptions: {
          data,
          columns,
          state: {
            sorting,
            isLoading: loading,
          },
          manualSorting: true,
          enableSorting: true,
          enableMultiSort: false,
          enableSortingRemoval: false,
          getCoreRowModel: getCoreRowModel(),
          onSortingChange,
        },
      }}
      slotProps={{
        container: { ref: containerRef, onScroll },
        bodyCell: { className: "align-top" },
        bodyRow: {
          onClick: (row) => onRowClick?.(row as Row<InventoryByProductItem>),
          className: "hover:bg-primary-100 cursor-pointer",
        },
      }}
      noDataMessage='No product inventory records found'
    />
  )
}
